import { styles as buttonStyles } from '@orn/shared-web/components/Button/Button';
import { Label, PhoneNumberInput } from '@orn/shared-web/components/Input';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip';
import { PhoneHandsetIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';

import { verifyState } from './verifiablePhoneState';

import type { IPhoneValidationResult } from '@orn/shared-web/components/Input/PhoneNumberInput';

/**
 * What saving a changed number costs.
 *
 * One string for every phone field in Settings, because it is one consequence:
 * the write clears `phone_number_verified_at`, so a number that was reachable a
 * moment ago stops being.
 */
export const RE_VERIFY_NOTE = "You'll need to re-verify this phone number after this change.";

/** Why the button reports rather than offers while a handshake is out. */
const RUNNING_TOOLTIP = 'Waiting on your group text. Check your phone messages for instructions.';

export interface IVerifiablePhoneFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  hint?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  /**
   * Help text behind the field's own `?`, for when the field is held back by a
   * rule rather than a transient lock. A tooltip rather than a line under the
   * input, so the field does not grow a second permanent subtitle beside its
   * hint.
   */
  tooltip?: string;
  /** Overrides the input's own format check; used to surface a server refusal. */
  validation?: IPhoneValidationResult;
  /** When they last proved they hold the number, or null when nobody has. */
  verifiedAt: string | null;
  /** Whether the typed number differs from the one on file. */
  isChanged: boolean;
  /** Suppresses the re-verify note while the field is already reporting an error. */
  hasError?: boolean;
  /** Start a verification. Absent on a surface that cannot. */
  onVerify?: () => void;
  /** Why the offer exists and what it costs — the button's own tooltip. */
  verifyTooltip: string;
  /** A verification already out on other handsets, or one this client just sent. */
  isVerificationRunning?: boolean;
  /** A precondition this surface owns that has to be met first, or null. */
  verifyBlockedReason?: string | null;
}

/**
 * A phone number, and the offer to prove it reaches whoever owns it.
 *
 * One component for both the account holder's own number and a loved one's,
 * because the two say the same things and had drifted apart every time either
 * was touched: the same warning that a change costs the existing verification,
 * the same button, the same rule for when the button is withdrawn.
 *
 * **The button's presence is the whole report.** A proven number simply has no
 * button, and an unproven one has one — there is no accompanying "Verified" or
 * "not confirmed yet" line, which would repeat what the button already says and
 * turn every unproven number into a standing warning.
 *
 * The label sits outside the input row rather than on the input, so the button
 * aligns to the field's top edge without being nudged past a label box. That
 * puts it outside the input's `TextField`, which is what `ariaLabel` is for —
 * react-aria associates only an inner `Label`, so the visible text has to be
 * handed to the input a second way or it has no accessible name at all.
 */
const VerifiablePhoneField = ({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  hint,
  isRequired,
  isDisabled,
  tooltip,
  validation,
  verifiedAt,
  isChanged,
  hasError,
  onVerify,
  verifyTooltip,
  isVerificationRunning,
  verifyBlockedReason,
}: IVerifiablePhoneFieldProps) => {
  const state = verifyState({
    canStart: Boolean(onVerify),
    verifiedAt,
    value,
    isChanged,
    isRunning: Boolean(isVerificationRunning),
    blockedReason: verifyBlockedReason ?? null,
  });

  const buttonLabel = state.kind === 'running' ? 'Verifying…' : 'Verify';
  const buttonTooltip =
    state.kind === 'running' ? RUNNING_TOOLTIP : state.kind === 'blocked' ? state.reason : verifyTooltip;
  const isInert = state.kind === 'running' || state.kind === 'blocked';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1.5">
        <Label isRequired={isRequired}>{label}</Label>
        <div className="flex items-start gap-2">
          <PhoneNumberInput
            label=""
            ariaLabel={label}
            icon={PhoneHandsetIcon}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            hint={hint}
            isRequired={isRequired}
            isDisabled={isDisabled}
            validation={validation}
            tooltip={tooltip}
            className="flex-1"
          />
          {state.kind !== 'hidden' && (
            <Tooltip title={buttonTooltip} placement="top">
              <TooltipTrigger
                // An inert button keeps taking hover and focus, because its
                // tooltip is the only thing that says why it is inert —
                // `isDisabled` would take the explanation away with the action.
                onPress={isInert ? undefined : onVerify}
                aria-disabled={isInert}
                className={cx(
                  buttonStyles.common.root,
                  buttonStyles.sizes.md.root,
                  buttonStyles.colors.primary.root,
                  'h-11 shrink-0 rounded-lg',
                  isInert && 'cursor-default opacity-50'
                )}
              >
                <span className="px-0.5">{buttonLabel}</span>
              </TooltipTrigger>
            </Tooltip>
          )}
        </div>
      </div>
      {isChanged && !hasError && <span className="text-warning-primary text-xs font-medium">{RE_VERIFY_NOTE}</span>}
    </div>
  );
};

VerifiablePhoneField.displayName = 'VerifiablePhoneField';

export default VerifiablePhoneField;
